import {
	useBlockProps,
	useInnerBlocksProps,
	RichText,
	InspectorControls,
} from '@wordpress/block-editor';
import {
	PanelBody,
	Button,
	TextControl,
	ToggleControl,
	DateTimePicker,
} from '@wordpress/components';
import { useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import IconsModal from '../../components/IconsModal';

const CONTENT_TEMPLATE = [
	[ 'core/heading', { level: 4, placeholder: 'Event title' } ],
	[ 'core/paragraph', { placeholder: 'Event description...' } ],
];

export default function Edit( { attributes, setAttributes, context } ) {
	const { date, iconName, iconType, highlighted, markerLabel } = attributes;
	const [ isIconsModalOpen, setIsIconsModalOpen ] = useState( false );

	const markerSize = context[ 'brandy/timelineMarkerSize' ] || 26;
	// Fallbacks mirror the token defaults in Timeline's block.json, for the
	// case where an item renders before parent context has resolved.
	const markerColor =
		context[ 'brandy/timelineMarkerColor' ] ||
		'var(--wp--preset--color--brandy-primary, #0061FE)';
	const markerBg =
		context[ 'brandy/timelineMarkerBgColor' ] ||
		'var(--wp--custom--color--background, #FFFFFF)';

	const blockProps = useBlockProps( {
		className: `brandy-timeline-item${
			highlighted ? ' brandy-timeline-item--highlighted' : ''
		}`,
	} );

	const contentProps = useInnerBlocksProps(
		{ className: 'brandy-timeline-item__content' },
		{ template: CONTENT_TEMPLATE }
	);

	// Build marker content.
	const iconClass = iconName
		? `ph${ iconType !== 'regular' ? '-' + iconType : '' } ph-${ iconName }`
		: '';

	let markerContent;
	if ( iconName ) {
		markerContent = (
			<i
				className={ `brandy-timeline-item__marker-icon ${ iconClass }` }
			/>
		);
	} else if ( markerLabel ) {
		markerContent = (
			<span className="brandy-timeline-item__marker-label">
				{ markerLabel }
			</span>
		);
	} else {
		markerContent = (
			<span className="brandy-timeline-item__marker-dot" />
		);
	}

	const markerStyle = {
		width: markerSize + 'px',
		height: markerSize + 'px',
		borderColor: markerColor,
		backgroundColor: markerBg,
	};

	return (
		<>
			<InspectorControls>
				<PanelBody
					title={ __( 'Item Settings', 'brandy-blocks' ) }
				>
					<ToggleControl
						__nextHasNoMarginBottom
						label={ __( 'Highlighted', 'brandy-blocks' ) }
						checked={ highlighted }
						onChange={ ( v ) =>
							setAttributes( { highlighted: v } )
						}
					/>
					<DateTimePicker
						currentDate={ null }
						onChange={ ( newDate ) => {
							if ( newDate ) {
								const d = new Date( newDate );
								const formatted = d.toLocaleDateString(
									'en-US',
									{
										year: 'numeric',
										month: 'long',
										day: 'numeric',
									}
								);
								setAttributes( { date: formatted } );
							}
						} }
						is12Hour={ false }
						__nextRemoveHelpButton
						__nextRemoveResetButton
					/>
					<style>{ `.components-datetime__time { display: none; }` }</style>
					<TextControl
						__nextHasNoMarginBottom
						label={ __( 'Marker label', 'brandy-blocks' ) }
						value={ markerLabel }
						onChange={ ( v ) =>
							setAttributes( { markerLabel: v } )
						}
						help={ __(
							'Custom text in marker (overrides dot)',
							'brandy-blocks'
						) }
					/>
					<div
						style={ {
							display: 'flex',
							alignItems: 'center',
							gap: '12px',
							marginTop: '12px',
						} }
					>
						{ iconName && (
							<i
								className={ iconClass }
								style={ { fontSize: '24px' } }
							/>
						) }
						<Button
							variant="secondary"
							onClick={ () => setIsIconsModalOpen( true ) }
						>
							{ iconName
								? __( 'Change icon', 'brandy-blocks' )
								: __( 'Choose icon', 'brandy-blocks' ) }
						</Button>
						{ iconName && (
							<Button
								variant="tertiary"
								isDestructive
								onClick={ () =>
									setAttributes( { iconName: '' } )
								}
							>
								{ __( 'Remove', 'brandy-blocks' ) }
							</Button>
						) }
					</div>
				</PanelBody>
			</InspectorControls>
			<div { ...blockProps }>
				<div
					className="brandy-timeline-item__marker"
					style={ markerStyle }
				>
					{ markerContent }
				</div>
				<RichText
					tagName="div"
					className="brandy-timeline-item__date"
					value={ date }
					onChange={ ( v ) => setAttributes( { date: v } ) }
					placeholder={ __( 'Date...', 'brandy-blocks' ) }
				/>
				<div { ...contentProps } />
			</div>
			<IconsModal
				isIconsModalOpen={ isIconsModalOpen }
				setIsIconsModalOpen={ setIsIconsModalOpen }
				icon={ {
					name: iconName,
					type: iconType,
				} }
				setAttributes={ ( newAttrs ) =>
					setAttributes( {
						iconName: newAttrs.name,
						iconType: newAttrs.type,
					} )
				}
			/>
		</>
	);
}
